/**
 * Merchant Pro V1 PR-4b — Notifications full-route page.
 *
 * /owner/notifications — denser list view of the same projection.
 * Same filter chips · same mark-read mechanics · 100 items per page.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2, MailOpen, CalendarDays, ListChecks, Settings as SettingsIcon, Inbox, ChevronRight,
} from 'lucide-react';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';

const CATEGORY_CHIPS = [
  { key: null,           label: 'All',          icon: Inbox },
  { key: 'reservation',  label: 'Reservations', icon: CalendarDays },
  { key: 'waitlist',     label: 'Waitlist',     icon: ListChecks },
  { key: 'system',       label: 'System',       icon: SettingsIcon },
];

function _relativeTime(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function NotificationsPage() {
  const { token } = useAuthStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [unreadOnly, setUnreadOnly] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: qk.ownerNotifications({ category, unread_only: unreadOnly, lookback_days: 7, page: 1 }),
    queryFn: () => api.ownerNotifications(token, {
      category: category || undefined,
      unread_only: unreadOnly || undefined,
      lookback_days: 7,
      limit: 100,
    }),
    enabled: !!token,
    refetchInterval: 30_000,
  });

  const items = useMemo(() => data?.items ?? [], [data]);
  const unread = data?.unread_count ?? 0;
  const total = data?.total_count ?? 0;

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['owner', 'notifications'] });
  };
  const markReadM = useMutation({
    mutationFn: (id) => api.ownerNotificationsMarkRead(token, { notification_ids: [id] }),
    onSuccess: invalidateAll,
  });
  const markAllM = useMutation({
    mutationFn: () => api.ownerNotificationsMarkRead(token, { mark_all: true }),
    onSuccess: invalidateAll,
  });

  const onRowClick = async (row) => {
    if (!row.is_read) { try { await markReadM.mutateAsync(row.id); } catch { /* ok */ } }
    if (row.target_url) navigate(row.target_url);
  };

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-gold/85 font-semibold">
            Merchant Pro V1 · PR-4b
          </div>
          <h1 className="text-2xl font-semibold mt-1" data-testid="notifications-page-title">
            Notifications
          </h1>
          <p className="text-sm text-white/55 mt-1">
            Read-model feed over the last 7 days of audit events. Polls every 30 seconds.
            {total > 0 ? ` · ${total} events · ${unread} unread` : null}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs font-semibold text-white/70">
            <input
              type="checkbox"
              checked={unreadOnly}
              onChange={(e) => setUnreadOnly(e.target.checked)}
              data-testid="notifications-page-unread-only"
            />
            Unread only
          </label>
          <button
            type="button"
            onClick={() => markAllM.mutate()}
            disabled={markAllM.isPending || unread === 0}
            data-testid="notifications-page-mark-all"
            className="flex items-center gap-2 px-3 py-2 rounded-2xl bg-[#15151B] border border-white/10 text-sm text-white/80 hover:bg-white/5 disabled:opacity-50"
          >
            <MailOpen size={14} />
            Mark all read
          </button>
        </div>
      </div>

      <div
        className="flex flex-wrap gap-2"
        data-testid="notifications-page-filter-chips"
      >
        {CATEGORY_CHIPS.map((c) => {
          const Icon = c.icon;
          const active = (category ?? null) === (c.key ?? null);
          return (
            <button
              key={c.label}
              type="button"
              onClick={() => setCategory(c.key)}
              data-testid={`notifications-page-chip-${c.key ?? 'all'}`}
              data-active={active ? 'true' : 'false'}
              className={
                'flex items-center gap-2 px-3 py-1.5 rounded-2xl text-sm font-medium border transition-colors ' +
                (active
                  ? 'bg-gold/15 text-gold border-gold/40'
                  : 'bg-[#15151B] text-white/70 border-white/10 hover:bg-white/5')
              }
            >
              <Icon size={14} />
              {c.label}
            </button>
          );
        })}
      </div>

      <div className="rounded-2xl bg-[#0E0E13] border border-white/5 overflow-hidden">
        {isLoading ? (
          <div data-testid="notifications-page-loading" className="text-white/50 text-sm py-16 text-center">
            Loading notifications…
          </div>
        ) : items.length === 0 ? (
          <div
            data-testid="notifications-page-empty"
            className="text-white/40 text-sm py-16 text-center px-6"
          >
            <Inbox size={32} className="mx-auto mb-3 opacity-60" />
            <div className="font-semibold text-base">No notifications</div>
            <div className="mt-1">Operational events for the last 7 days will surface here.</div>
          </div>
        ) : (
          <ul className="divide-y divide-white/5">
            {items.map((row) => (
              <li
                key={row.id}
                data-testid={`notifications-page-row-${row.id}`}
                data-category={row.category}
                data-is-read={row.is_read ? 'true' : 'false'}
                className={
                  'group flex items-start gap-3 px-5 py-3.5 hover:bg-white/[0.03] cursor-pointer ' +
                  (row.is_read ? 'opacity-65' : '')
                }
                onClick={() => onRowClick(row)}
              >
                <span
                  className="mt-1.5 w-2 h-2 rounded-full shrink-0"
                  style={{ background: row.is_read ? 'transparent' : '#D4AF37' }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <CategoryPill category={row.category} />
                    <span className="text-[11px] text-white/40 font-mono">{_relativeTime(row.created_at)}</span>
                  </div>
                  <div className="text-sm font-medium text-white/95 mt-1 leading-snug">{row.title}</div>
                  {row.body ? (
                    <div className="text-xs text-white/55 mt-0.5">{row.body}</div>
                  ) : null}
                </div>
                <div className="flex items-center gap-1 shrink-0 self-center opacity-0 group-hover:opacity-100">
                  {!row.is_read ? (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); markReadM.mutate(row.id); }}
                      disabled={markReadM.isPending}
                      data-testid={`notifications-page-row-${row.id}-mark-read`}
                      className="p-1.5 rounded-full text-white/70 hover:bg-white/5"
                      title="Mark as read"
                    >
                      <CheckCircle2 size={14} />
                    </button>
                  ) : null}
                  <ChevronRight size={14} className="text-white/40" />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function CategoryPill({ category }) {
  const color = {
    reservation: '#FBBF24',
    waitlist:    '#3B82F6',
    system:      '#F97316',
  }[category] || '#FFFFFF';
  return (
    <span
      className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-md"
      style={{ background: color + '22', color, border: `1px solid ${color}55` }}
    >
      {category}
    </span>
  );
}
